<template>
  <ContentTemplate :title="mode === 'DELETE' ? 'Delete Workspace' : 'Leave Workspace'" :container-size-small="true">
    <form>

      <div class="form-group">
        <div class="row">
          <div class="col-4">
            <FormGroupLabelSection
                label="Workspaces"
                icon="User"
                icon-type="fal"
            />
          </div>
          <div class="col-8">
            <WorkspaceAccessSection :workspace="workspace"
                                    :with-button="false" :is-disable="true"
            />
          </div>
        </div>
      </div>

      <FormGroupSection
          :form-element="currentPasswordInput"
          v-model:input-value="form[currentPasswordInput.input.name]"
          :validator="v$.form[currentPasswordInput.input.name]"
      />

      <div class="fp-form-group">
        <div class="row mb-3">
          <div class="col-4"></div>
          <div class="col-8">
            <div class="attention">
              <template v-if="mode==='DELETE'">
                <p><b>Warning:</b></p>
                <p>The workspace is about to be deleted. This action cannot be undone. Confirm before proceeding.</p>
                <br/>
                <p>The workspace, its associated link, and all projects within will be permanently deleted.</p>
                <br/>
                <p style="color: var(--text-dark);">The account and profile data will remain accessible. Other workspaces where membership exists will not be affected.</p>
              </template>
              <template v-if="mode==='LEFT'">
                <p><b>Warning:</b></p>
                <p>After leaving, rejoining will require a new invitation from an admin.</p>
              </template>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-4"></div>
          <div class="col-8 d-flex justify-content-end mt-2">
            <ButtonElement v-if="mode==='LEFT'"
                           class="btn--ms btn--accent"
                           @clickOnButton="openConfirmationPopup()">
              Leave
            </ButtonElement>
            <ButtonElement v-if="mode==='DELETE'"
                           class="btn--ms btn--danger"
                           @clickOnButton="sendDeleteWorkspaceMail()">
              Delete now
            </ButtonElement>
          </div>
        </div>
      </div>

    </form>
  </ContentTemplate>

</template>
<script lang="ts">
import {defineComponent} from 'vue'
import {AccountNs, AuthenticationNs, CommonNs, type PopupNs, WorkspaceNs} from '@shared/types'
import {
  ButtonElement,
  ContentTemplate,
  FormGroupLabelSection,
  FormGroupSection,
  WorkspaceAccessSection,
} from "@shared/components";

import useVuelidate from '@vuelidate/core'
import {required} from '@vuelidate/validators'
import {mapStores} from "pinia";
import {useWorkspaceStore} from "@shared/stores/workspace.store";
import {useAccountStore} from "@domains/account/stores/account.store";
import {useGeneralStore} from "@shared/stores/general.store";
import {toast} from '@shared/ms-components'

export default defineComponent({
  name: 'WorkspaceView',
  components: {
    ButtonElement,
    ContentTemplate,
    FormGroupSection,
    FormGroupLabelSection,
    WorkspaceAccessSection
  },
  computed: {
    ...mapStores(useWorkspaceStore, useAccountStore, useGeneralStore),
  },
  data() {
    return {
      form: {} as { [key: string]: string },
      currentPasswordInput: {} as CommonNs.InputFormGroup,
      workspace: {} as WorkspaceNs.Workspace,
      title: '',
      warning: '',
      mode: 'LEFT',
      workspaceId: '-1'
    }
  },
  setup() {
    return {v$: useVuelidate()}
  },
  validations() {
    return {
      form: {
        currentPassword: {
          required
        }
      }
    }
  },

  created() {
    if ((this.$route.meta.data as AccountNs.WorkspaceRouteMetaData)) {
      const data = this.$route.meta.data as AccountNs.WorkspaceRouteMetaData
      this.mode = data.mode
    }

    if (this.$route.params) {
      this.workspaceId = this.$route.params.workspaceId
    }
  },

  beforeMount(): void {
    this.buildForm()
  },

  methods: {
    buildForm(): void {
      const generalInfo = this.accountStore.getGeneralInfo
      this.workspace = generalInfo.workspaces.find((workspace: WorkspaceNs.Workspace) => (workspace.id === this.workspaceId))

      this.form = {
        currentPassword: ''
      }

      this.currentPasswordInput = {
        label: 'Current Password',
        iconType: 'fal',
        icon: 'Lock',
        input: {
          name: 'currentPassword',
          type: 'password',
          placeholder: 'Current Password',
          withButton: true
        }
      } as CommonNs.InputFormGroup
    },

    leftWorkspace(): void {
      this.v$.$touch()
      // @ts-ignore
      if (this.v$.form.$invalid) {
        return
      }

      const payload = {
        workspaceId: this.workspaceId,
        password: this.form.currentPassword
      } as WorkspaceNs.WorkspaceActionRequest

      this.workspaceStore.leftWorkspace(payload).then((response) => {
        this.accountStore.loadGeneralInfo();

        toast.success('Left the workspace');

        if (response.result === true) {
          if (this.workspaceId === this.workspaceStore.getSelectedWorkspaceId) {
            this.$router.push({name: AuthenticationNs.AuthenticationRouteEnums.ExistingWorkspaces})
          } else {
            this.goToGeneralPage()
          }
        }
      })
    },

    sendDeleteWorkspaceMail(): void {
      this.v$.$touch()
      // @ts-ignore
      if (this.v$.form.$invalid) {
        return
      }

      const payload = {
        workspaceId: this.workspaceId,
        password: this.form.currentPassword
      } as WorkspaceNs.WorkspaceActionRequest

      this.workspaceStore.sendDeleteWorkspaceMail(payload).then(() => {
        toast.success('Confirmation email sent. Confirm to delete workspace');

        this.goToGeneralPage()
      })
    },

    goToGeneralPage() {
      this.$router.push({name: AccountNs.AccountRouteEnums.GENERAL})
    },

    openConfirmationPopup(): void {
      this.v$.$touch()
      // @ts-ignore
      if (this.v$.form.$invalid) {
        return
      }

      const payload = {
        content: {
          title: 'Leave Workspace',
          message: '<p>Are you sure you want to leave this workspace?</p> ' +
              '<p>To rejoin this workspace, you will need a new invitation from an administrator or workspace owner.</p>'
        },
        callback: {
          content: 'Leave now',
          function: this.leftWorkspace
        }
      } as PopupNs.Popup

      this.generalStore.showPopup(payload)
    }
  }
})
</script>

<style lang="scss" scoped>

hr {
  margin: 30px 0;
}

.fp-form-group {
  & .description {
    font-size: var(--fs-14);
    color: var(--text-neutral);
    margin: var(--space-0);

    p {
      font-size: var(--fs-12);
      color: var(--text-neutral);
      margin: var(--space-0);
    }
  }

  & .attention {
    font-size: var(--fs-12);
    color: var(--red-100);
    margin: var(--space-0);

    p {
      font-size: var(--fs-12);
      color: var(--red-100);
      margin: var(--space-0);
    }
  }
}
</style>
